import React, { JSX } from "react";
import { Document, Page } from "react-pdf";
import { Link } from "react-router";
import { ClipLoader } from "react-spinners";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { Carousel } from "@domain/carousel/carousel";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useHover } from "@utils/hooks";
import { theme } from "@ui/styles/theme";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { CarouselIcon } from "@ui/components/icons";
import { Row } from "@ui/components/layout";
import { pdfDocumentOptions } from "@ui/components/media/pdf/pdfDocumentOptions";
import { UserView } from "@ui/components/profile/customUserView";

interface CarouselThumbnailProps {
	carousel: Carousel;
	displayAuthorAsMe?: boolean;
	big?: boolean;
	medium?: boolean;
}

const renderCourouselBadge = (): JSX.Element => {
	return (
		<MainBadgeContainer bgColor={theme.colors.secondary}>
			<StyledIcons>
				<CarouselIcon />
			</StyledIcons>
		</MainBadgeContainer>
	);
};

export const CarouselThumbnail: React.FC<CarouselThumbnailProps> = ({
	carousel,
	displayAuthorAsMe,
	big,
	medium,
	...htmlProps
}) => {
	const displayedAuthor = getDisplayedAuthor(carousel);
	const carouselWidth = big ? 482.5 : medium ? 435 : 316;
	const { ref, isHovered } = useHover();
	return carousel && carousel.asset ? (
		<Container {...htmlProps} ref={ref} carouselWidth={carouselWidth}>
			<Link to={Path.ReferenceCarousel({ id: carousel.id, slug: carousel.slug, category: carousel.category })}>
				<StyledPdfRow>
					<PageWrapper>
						<Document file={carousel.asset.url} options={pdfDocumentOptions} loading={<ClipLoader />}>
							<Page
								key={1}
								loading={
									<LoadingWrapper height={276}>
										<ClipLoader />
									</LoadingWrapper>
								}
								pageNumber={1}
								renderAnnotationLayer={false}
								renderTextLayer={false}
								width={carouselWidth}
								height={316}
							/>
						</Document>
					</PageWrapper>

					<HoveredContainer isHovered={isHovered} carouselWidth={carouselWidth}>
						<StyledUserView user={displayedAuthor} carousel={carousel} />
						<Category category={carousel.category} />
						<IconsContainer>{renderCourouselBadge()}</IconsContainer>
					</HoveredContainer>
				</StyledPdfRow>
			</Link>
		</Container>
	) : (
		<></>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "carouselWidth",
})<{ carouselWidth: number }>`
	background-color: white;
	width: ${({ carouselWidth }) => `${carouselWidth}px`};
	height: 316px;
	border-radius: 10px;
`;

const Category = styled(CategoryLabel)`
	position: absolute;
	top: 10px;
	left: 10px;
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	font-size: 9px;
	line-height: 11px;
	color: #ffffff;
`;

const StyledPdfRow = styled(Row)`
	position: relative;
	overflow: hidden;
	height: 316px;
	border-radius: 10px;
`;

const PageWrapper = styled.div`
	position: absolute;
`;

const HoveredContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => !["isHovered", "carouselWidth"].includes(prop),
})<{ isHovered?: boolean; carouselWidth: number }>`
	position: relative;
	width: ${({ carouselWidth }) => `${carouselWidth}px`};
	height: 316px;
	z-index: 1;
	opacity: 0;
	background-color: rgba(0, 0, 0, 0.5);
	${({ isHovered }) =>
		isHovered
			? css`
					opacity: 1;
					animation-duration: 1s;
					animation-name: fadein;
				`
			: css`
					animation-duration: 1s;
					animation-name: fadeout;
					opacity: 0;
				`};

	@keyframes fadein {
		from {
			opacity: 0;
		}

		to {
			opacity: 1;
		}
	}

	@keyframes fadeout {
		from {
			opacity: 1;
		}

		to {
			opacity: 0;
		}
	}
`;

const LoadingWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "height",
})<{ height: number }>`
	width: 100%;
	height: ${({ height }) => height}px;
	border-top-left-radius: 10px;
	border-top-right-radius: 10px;
`;

const MainBadgeContainer = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "bgColor",
})<{ bgColor: string }>`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ bgColor }) => bgColor};
	width: 47px;
	height: 47px;
	border-radius: 50%;
`;

const StyledIcons = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #fff;
	border-radius: 50%;
	width: 31px;
	height: 31px;
`;

const StyledUserView = styled(UserView)`
	position: absolute;
	bottom: 10px;
	left: 10px;
	color: #fff;
	font-weight: 300 !important;
`;

const IconsContainer = styled.div`
	position: absolute;
	left: 42%;
	top: 42%;
	margin: auto;
`;
